import type { SxProps, Theme } from '@mui/material/styles';

import { FIGMA_REF } from '../../layout';

/**
 * Hero — responsive. Figma 136:38330 guides desktop proportions only.
 * Text is top-left weighted (not vertically centered).
 */
export const heroStyles: Record<string, SxProps<Theme>> = {
  root: {
    position: 'relative',
    overflow: 'hidden',
    // Use tighter gutters on tablet; preserve Figma's 1380px width at 1440px.
    width: {
      xs: '100%',
      md: 'calc(100% - 16px)',
      // Figma uses 30px gutters on a 1440px canvas (~2.08% per side).
      // Preserve that proportion on wider desktop screens instead of
      // imposing a fixed 1380px maximum.
      lg: 'calc(100% - clamp(32px, 4.1667vw, 96px))',
    },
    maxWidth: 'none',
    marginInline: 'auto',
    // Desktop height is a ratio of the hero's own width (never viewport-height
    // based — vh behaves oddly on mobile as the address bar collapses/
    // expands). Mobile is content-driven instead: the copy (heading + lede)
    // varies enough between viewports that a fixed aspect ratio clipped the
    // paragraph against the overlapping search card at some widths.
    aspectRatio: { xs: 'auto', md: `1 / ${FIGMA_REF.hero.heightToWidth}` },
    minHeight: 0,
    // Safety cap only on desktop — on an unusually wide-but-short window the
    // width-driven aspect ratio could otherwise grow tall enough to push
    // the search bar past the fold. Mobile has no cap since it's already
    // sized to its own content, not a ratio that could overshoot.
    maxHeight: { xs: 'none', md: '82vh' },
    borderRadius: {
      xs: '12px',
      md: `clamp(12px, 1.4vw, ${FIGMA_REF.hero.radius}px)`,
    },
    isolation: 'isolate',
  },
  media: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    // Image is pre-masked to the hero ratio, so cover barely crops.
    // Anchor to the bottom to keep the full skyline in frame.
    objectFit: 'cover',
    objectPosition: 'center bottom',
    zIndex: 0,
    pointerEvents: 'none',
  },
  // Mobile only: the photo only has a light, legible band near its own top
  // (visible on desktop, where a fixed aspect ratio always keeps the copy
  // within it) — on mobile the hero's height instead grows with the
  // (variable-length) copy, so depending on exact viewport width the
  // paragraph can wrap onto the busy building silhouette lower in the
  // image. This fades the image to light behind wherever the text actually
  // ends up, regardless of how many lines it wraps to.
  scrim: {
    display: { xs: 'block', md: 'none' },
    position: 'absolute',
    inset: 0,
    zIndex: 0,
    pointerEvents: 'none',
    background:
      'linear-gradient(180deg, rgba(255,255,255,0.94) 0%, rgba(255,255,255,0.94) 55%, rgba(255,255,255,0) 82%)',
  },
  container: {
    position: 'relative',
    zIndex: 1,
    boxSizing: 'border-box',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    // Pull copy toward the top of the hero.
    paddingTop: { xs: '8%', md: 'clamp(36px, 8%, 64px)' },
    // Clearance for the search bar that overlaps the bottom edge
    paddingBottom: { xs: '22%', md: '20%' },
    paddingLeft: { xs: '24px', md: 'clamp(56px, 12%, 184px)' },
    paddingRight: { xs: '20px', md: '5%' },
    gap: { xs: '28px', md: '52px' },
    // Fill the hero; the title/lede below own their own widths so the
    // copy wraps like Figma regardless of viewport.
  },
  titleLine: (theme) => ({
    ...theme.typography.display,
    // Figma: Manrope Medium 64 / 56 on desktop.
    fontSize: {
      xs: '2rem',
      sm: '2.75rem',
      md: 'clamp(2.75rem, 5vw, 3.5rem)',
      lg: '4rem',
    },
    lineHeight: { xs: 1.15, md: 1, lg: '3.5rem' },
    color: theme.palette.common.black,
    margin: 0,
    // Desktop wraps only at the forced <br>; mobile wraps naturally.
    whiteSpace: { xs: 'normal', md: 'nowrap' },
  }),
  titleAccent: (theme) => ({
    ...theme.typography.displayItalic,
    fontSize: 'inherit',
    lineHeight: 'inherit',
    color: theme.palette.primary.main,
  }),
  lede: (theme) => ({
    ...theme.typography.body1,
    fontSize: { xs: '0.9375rem', md: '1.0625rem' },
    color: theme.palette.text.primary,
    // Let each sentence run full-length on desktop (Figma allows the
    // wider measure); wrap naturally on mobile.
    maxWidth: { xs: '100%', md: 'none' },
    whiteSpace: { xs: 'normal', md: 'nowrap' },
    margin: 0,
  }),
  // The manual line break only applies from md up; on mobile the copy
  // reflows on its own, so the <br> is suppressed.
  forcedBreak: {
    display: { xs: 'none', md: 'inline' },
  },
};
